"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { handleAnchorScroll } from "../scrollToAnchor"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

const MAX_LOGOS = 8

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 120px;
  }
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  width: 100%;
  /* content caps at 1440 (big desktop); at <=1440 viewports it shrinks to ~1200 with the section padding */
  max-width: 1440px;
  text-align: start;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`

/* text first in JSX = right column in RTL */
const TextColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Text = styled.p`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.6;
  color: var(--comp-text);
  margin: 24px 0 0;
  /* preserve any line breaks the editor typed in the textarea */
  white-space: pre-line;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    font-weight: 700;
  }
`

/* Filled CTA. Uses the package's heading colour as the fill and the section
   background as the label, so it is always a high-contrast solid button (dark
   button on a light section per Figma, and never blends when the inner package
   equals the section colour). */
const Cta = styled.a`
  display: inline-block;
  margin-top: 32px;
  padding: 12px 24px;
  border-radius: 8px;
  /* CTA text: 14px mobile, 20px desktop per Figma (ASH-1794) */
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  background-color: var(--comp-heading);
  color: var(--comp-bg);
  transition: opacity 0.2s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-heading);
    outline-offset: 2px;
  }
`

/* Two-column logo block. Fixed 600px on desktop (per Figma: logos take a fixed
   column, the text fills the rest); full width, 2-up on mobile. */
const LogoGrid = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 0 0 600px;
    width: 600px;
    gap: 24px 8px;
  }
`

/* Logos display at a uniform 158x59 (upload is 2x: 316x118) and are contained,
   so mixed source sizes never distort. */
const LogoCell = styled.div`
  position: relative;
  width: 100%;
  max-width: 158px;
  aspect-ratio: 316 / 118;
  margin: 0 auto;
`

// --- Main Component ---

function AshtromCompanies({ data }: Props) {
  const analytics = useLandingAnalytics()
  const headline = extractProperty(data, "headline") as string | ""
  const text = extractProperty(data, "text") as string | ""
  const ctaLinks = extractProperty(data, "ctaButton") as any[] | ""
  const cta = (Array.isArray(ctaLinks) && ctaLinks[0]) || null
  const logos = (extractProperty(data, "logos", true) as any[] | undefined) || []
  const visibleLogos = logos.filter((logo) => logo?.url).slice(0, MAX_LOGOS)

  if (!headline && !text && !cta && visibleLogos.length === 0) return null

  return (
    <Section>
      <Inner>
        <TextColumn>
          {headline && <Headline>{headline}</Headline>}
          {text && <Text>{text}</Text>}
          {cta?.url && cta?.name && (
            <Cta
              href={getLinkHrefFromMultiUrlPickerItem(cta)}
              onClick={(e) => {
                analytics.ctaClick(cta.name, LANDING_ELEMENTS.ashtromCompanies)
                handleAnchorScroll(e, getLinkHrefFromMultiUrlPickerItem(cta))
              }}
              target={cta.target || undefined}
              rel={cta.target === "_blank" ? "noopener noreferrer" : undefined}
            >
              {cta.name}
            </Cta>
          )}
        </TextColumn>
        {visibleLogos.length > 0 && (
          <LogoGrid>
            {visibleLogos.map((logo, i) => (
              <LogoCell key={i}>
                <Image
                  src={getUmbracoMediaUrl(logo.url)}
                  fill
                  alt={logo.altText || ""}
                  style={{ objectFit: "contain" }}
                />
              </LogoCell>
            ))}
          </LogoGrid>
        )}
      </Inner>
    </Section>
  )
}

export default AshtromCompanies
